export const STORY_CREATION_COST = 5;
// Translating a story into another language costs the same as writing a new
// one, because it costs *us* the same: a translation re-runs the whole
// pipeline, and ElevenLabs re-synthesizes the entire story at ~90% of the
// bill. There is no cheap half to discount.
//
// Raising this above STORY_CREATION_COST needs a change in cleanup.ts first:
// the stale-job sweep refunds a hard-coded STORY_CREATION_COST, so a dearer
// translation would be silently under-refunded when a job is swept. Teach the
// sweep to refund by job kind before pricing the two apart.
export const TRANSLATION_COST = STORY_CREATION_COST;
function toDate(v) {
    if (!v)
        return undefined;
    if (v instanceof Date)
        return v;
    if (typeof v === "object" &&
        v !== null &&
        "toDate" in v &&
        typeof v.toDate === "function") {
        return v.toDate();
    }
    if (typeof v === "string" || typeof v === "number")
        return new Date(v);
    return undefined;
}
export function entitlementsFor(customer, opts = {}) {
    const storyCost = opts.storyCost ?? STORY_CREATION_COST;
    const now = opts.now ?? new Date();
    if (!customer) {
        return {
            hasActiveSubscription: false,
            isTrialing: false,
            isGod: false,
            hasContentAccess: true,
            contentAccessReason: "no_customer",
            canCompose: false,
            composeBlockedReason: "no_customer",
            canShareStory: false,
            canDownloadOffline: false,
            canBrowseFullCatalog: false,
            canPrintStory: false,
            canCreatePlaylists: false,
            storyCost,
            creditsRemaining: 0,
        };
    }
    const isGod = customer.godMode === true;
    const status = customer.subscriptionStatus;
    const isActive = status === "active";
    const isTrialingStatus = status === "trialing";
    const isTrialing = isTrialingStatus;
    const hasActiveSubscription = isGod || isActive || isTrialingStatus;
    let hasContentAccess;
    let contentAccessReason;
    let contentAccessExpiresAt;
    const expiresAt = toDate(customer.contentAccessExpiresAt);
    if (isGod) {
        hasContentAccess = true;
        contentAccessReason = "god_mode";
    }
    else if (isActive || isTrialingStatus) {
        hasContentAccess = true;
        contentAccessReason = "active_subscription";
    }
    else if (customer.contentAccessLocked === true) {
        hasContentAccess = false;
        contentAccessReason = "expired";
        contentAccessExpiresAt = expiresAt;
    }
    else if (expiresAt) {
        if (now < expiresAt) {
            hasContentAccess = true;
            contentAccessReason = "valid_period";
            contentAccessExpiresAt = expiresAt;
        }
        else {
            hasContentAccess = false;
            contentAccessReason = "expired";
            contentAccessExpiresAt = expiresAt;
        }
    }
    else {
        hasContentAccess = true;
        contentAccessReason = "no_expiration";
    }
    const creditsRemaining = customer.creditsRemaining ?? 0;
    let canCompose;
    let composeBlockedReason;
    if (isGod) {
        canCompose = true;
    }
    else if (isTrialingStatus && customer.trialStoryUsed === true) {
        canCompose = false;
        composeBlockedReason = "trial_already_used";
    }
    else if (creditsRemaining >= storyCost) {
        canCompose = true;
    }
    else {
        canCompose = false;
        composeBlockedReason = "insufficient_credits";
    }
    // Subscription-gated capabilities collapse to the same predicate
    // today: only active subs (or godMode) get them. Pay-as-you-go
    // customers (pack credits, no sub) and lapsed subscribers in their
    // 180-day content-access grace period do NOT get these capabilities
    // — the grace period only preserves reading access to stories they
    // already own; it does not extend the premium feature set. Kept as
    // named fields so each can diverge later without touching call sites.
    const subGated = hasActiveSubscription;
    // Sharing is temporarily FREE FOR ALL signed-in users to drive traffic
    // (shared links pull new users into the public web app). To re-gate it
    // to subscribers later, change this back to `subGated`. Kept as its own
    // line precisely so that flip is one word.
    const canShareStory = true;
    return {
        hasActiveSubscription,
        isTrialing,
        isGod,
        hasContentAccess,
        contentAccessReason,
        contentAccessExpiresAt,
        canCompose,
        composeBlockedReason,
        canShareStory,
        canDownloadOffline: subGated,
        canBrowseFullCatalog: subGated,
        canPrintStory: subGated,
        canCreatePlaylists: subGated,
        storyCost,
        creditsRemaining,
    };
}
//# sourceMappingURL=entitlements.js.map